import axios from 'axios';
import { toast } from 'sonner';

// SEMPRE URL relativi - NESSUNA dipendenza da Emergent o altri servizi esterni
// In produzione su Aruba: baseURL = '/api' (relativo al dominio)
const api = axios.create({
  baseURL: '/api',
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors + normalizza sempre detail (evita crash su array/oggetti)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('token');
      window.location.href = '/login';
    }
    // FIX: FastAPI restituisce detail come array di oggetti su errori 422.
    // Se lasciato grezzo e passato a toast/React, causa crash "Objects are not valid as React child"
    // (la temuta pagina bianca). Qui lo normalizziamo in stringa leggibile.
    if (error.response?.data?.detail !== undefined) {
      const detail = error.response.data.detail;
      if (Array.isArray(detail)) {
        error.response.data.detail = detail
          .map((d) => {
            if (d == null) return '';
            if (typeof d === 'string') return d;
            if (typeof d === 'object') {
              const loc = Array.isArray(d.loc) ? d.loc.filter((x) => x !== 'body').join('.') : '';
              return loc ? `${loc}: ${d.msg || ''}` : (d.msg || JSON.stringify(d));
            }
            return String(d);
          })
          .filter(Boolean)
          .join(' · ');
      } else if (detail && typeof detail === 'object') {
        error.response.data.detail = detail.msg || JSON.stringify(detail);
      }
    }
    // Toast globale sui 403 di autorizzazione (es. azienda prova a cancellare)
    if (
      error.response?.status === 403 &&
      typeof error.response?.data?.detail === 'string' &&
      /autorizzato dall'amministratore/i.test(error.response.data.detail)
    ) {
      try { toast.error(error.response.data.detail, { duration: 6000 }); } catch (e) { /* ignore */ }
    }
    return Promise.reject(error);
  }
);

// PDV API
export const pdvApi = {
  getAll: () => api.get('/pdv'),
  getOne: (id) => api.get(`/pdv/${id}`),
  create: (data) => api.post('/pdv', data),
  update: (id, data) => api.put(`/pdv/${id}`, data),
  delete: (id) => api.delete(`/pdv/${id}`),
};

// Documents API
export const documentsApi = {
  getAll: (params) => api.get('/documents', { params }),
  getOne: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Training API
export const trainingApi = {
  getAll: (params) => api.get('/trainings', { params }),
  getOne: (id) => api.get(`/trainings/${id}`),
  create: (data) => api.post('/trainings', data),
  update: (id, data) => api.put(`/trainings/${id}`, data),
  delete: (id) => api.delete(`/trainings/${id}`),
};

// Checklist Templates API
export const checklistApi = {
  getTemplates: () => api.get('/checklist-templates'),
  createTemplate: (data) => api.post('/checklist-templates', data),
  deleteTemplate: (id) => api.delete(`/checklist-templates/${id}`),
};

// Safety Checks API
export const safetyChecksApi = {
  getAll: (params) => api.get('/safety-checks', { params }),
  getOne: (id) => api.get(`/safety-checks/${id}`),
  create: (data) => api.post('/safety-checks', data),
  getReportUrl: (id) => `/api/safety-checks/${id}/report`,
  sendReport: (id, email) => api.post(`/safety-checks/${id}/send-report?email_to=${encodeURIComponent(email)}`),
};

// PDV Rankings API
export const pdvRankingsApi = {
  getRankings: () => api.get('/pdv/rankings'),
};

// Scheduled Inspections API
export const scheduledInspectionsApi = {
  getAll: (params) => api.get('/scheduled-inspections', { params }),
  getOne: (id) => api.get(`/scheduled-inspections/${id}`),
  create: (data) => api.post('/scheduled-inspections', data),
  update: (id, data) => api.put(`/scheduled-inspections/${id}`, data),
  delete: (id) => api.delete(`/scheduled-inspections/${id}`),
  cancel: (id) => api.put(`/scheduled-inspections/${id}/cancel`),
  complete: (id, checkId) => api.put(`/scheduled-inspections/${id}/complete?check_id=${checkId}`),
};

// Notifications API
export const notificationsApi = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Users API (Super Admin only)
export const usersApi = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, newPassword) => api.put(`/users/${id}/reset-password?new_password=${encodeURIComponent(newPassword)}`),
};

// Pending Registrations API
export const registrationsApi = {
  getPending: () => api.get('/auth/pending-registrations'),
  approve: (id) => api.post(`/auth/approve-registration/${id}`),
  reject: (id) => api.post(`/auth/reject-registration/${id}`),
};

// Reports API
export const reportsApi = {
  getReport: (checkId) => api.get(`/safety-checks/${checkId}/report`, { responseType: 'text' }),
  getReportData: (checkId) => api.get(`/safety-checks/${checkId}/download-pdf`),
};

// Inspectors API
export const inspectorsApi = {
  getAll: () => api.get('/inspectors'),
  create: (data) => api.post('/inspectors', data),
  update: (id, data) => api.put(`/inspectors/${id}`, data),
  delete: (id) => api.delete(`/inspectors/${id}`),
};

// PDV Checklists Config API
export const pdvChecklistsApi = {
  getForPdv: (pdvId) => api.get(`/pdv-checklists/${pdvId}`),
  activate: (pdvId, templateId) => api.post(`/pdv-checklists/${pdvId}/activate/${templateId}`),
  deactivate: (pdvId, templateId) => api.post(`/pdv-checklists/${pdvId}/deactivate/${templateId}`),
};

// Non-conformity resolution API
export const resolveNonConformity = (checkId, data) => api.post(`/safety-checks/${checkId}/resolve-item`, data);

// Teachers API
export const teachersApi = {
  getAll: () => api.get('/teachers'),
  create: (data) => api.post('/teachers', data),
  update: (id, data) => api.put(`/teachers/${id}`, data),
  delete: (id) => api.delete(`/teachers/${id}`),
  uploadCv: (id, cvUrl) => api.put(`/teachers/${id}/cv?cv_url=${encodeURIComponent(cvUrl)}`),
};

// Personnel API
export const personnelApi = {
  getAll: (params) => api.get('/personnel', { params }),
  getOne: (id) => api.get(`/personnel/${id}`),
  create: (data) => api.post('/personnel', data),
  update: (id, data) => api.put(`/personnel/${id}`, data),
  delete: (id) => api.delete(`/personnel/${id}`),
  transfer: (id, targetPdvId) => api.post(`/personnel/${id}/transfer`, { target_pdv_id: targetPdvId }),
  withoutTraining: () => api.get('/personnel/without-training'),
};

// Certificate Templates API
export const certificateTemplatesApi = {
  getAll: () => api.get('/certificate-templates'),
  create: (data) => api.post('/certificate-templates', data),
  delete: (id) => api.delete(`/certificate-templates/${id}`),
};

// Certificates API
export const certificatesApi = {
  getAll: (params) => api.get('/certificates', { params }),
  getOne: (id) => api.get(`/certificates/${id}`),
  create: (data) => api.post('/certificates', data),
  verify: (code) => api.get(`/certificates/verify/${code}`),
  issue: (id) => api.put(`/certificates/${id}/issue`),
};

// Attendance Registers API
export const attendanceRegistersApi = {
  getAll: (params) => api.get('/attendance-registers', { params }),
  getOne: (id) => api.get(`/attendance-registers/${id}`),
  create: (data) => api.post('/attendance-registers', data),
  delete: (id) => api.delete(`/attendance-registers/${id}`),
  autoGenerate: (params) => api.post('/attendance-registers/auto-generate', null, { params }),
  updateParticipants: (id, updates) => api.put(`/attendance-registers/${id}/participants`, updates),
  markPrinted: (id) => api.put(`/attendance-registers/${id}/mark-printed`),
  submit: (id, fileUrl) => api.put(`/attendance-registers/${id}/submit${fileUrl ? `?file_url=${encodeURIComponent(fileUrl)}` : ''}`),
  getPrintUrl: (id) => {
    const token = sessionStorage.getItem('token');
    return `/api/attendance-registers/${id}/print?token=${token}`;
  },
  // v37 - firma digitale
  generateSigningTokens: (id) => api.post(`/attendance-registers/${id}/signing-tokens`),
  getSigningStatus: (id) => api.get(`/attendance-registers/${id}/signing-status`),
  getQrPdf: (id) =>
    api.get(`/attendance-registers/${id}/qr-pdf`, { responseType: 'blob' }),
};

// User Permissions API
export const userPermissionsApi = {
  update: (userId, data) => api.put(`/users/${userId}/permissions`, data),
};

// Virtual Classrooms API
export const virtualClassroomsApi = {
  getAll: () => api.get('/virtual-classrooms'),
  create: (data) => api.post('/virtual-classrooms', data),
  update: (id, data) => api.put(`/virtual-classrooms/${id}`, data),
  delete: (id) => api.delete(`/virtual-classrooms/${id}`),
};

// Training Slides API
export const trainingSlidesApi = {
  getAll: (params) => api.get('/training-slides', { params }),
  create: (data) => api.post('/training-slides', data),
  delete: (id) => api.delete(`/training-slides/${id}`),
};

// Teacher Invoices API
export const teacherInvoicesApi = {
  getAll: (params) => api.get('/teacher-invoices', { params }),
  create: (data) => api.post('/teacher-invoices', data),
  markPaid: (id) => api.put(`/teacher-invoices/${id}/mark-paid`),
  delete: (id) => api.delete(`/teacher-invoices/${id}`),
};

// Licenses API
export const licensesApi = {
  getAll: () => api.get('/licenses/all'),
  getMyLicense: () => api.get('/auth/my-license'),
  generate: (planType, userId) => api.post(`/licenses/generate?plan_type=${planType}${userId ? `&user_id=${userId}` : ''}`),
  getRenewalRequests: () => api.get('/licenses/renewal-requests'),
  completeRenewal: (requestId) => api.post(`/licenses/renewal-requests/${requestId}/complete`),
  requestRenewal: () => api.post('/auth/request-renewal'),
  create: (data) => api.post('/licenses', data),
  deactivate: (id) => api.put(`/licenses/${id}/deactivate`),
  extend: (id, days) => api.put(`/licenses/${id}/extend?days=${days}`),
  validate: (key) => api.post(`/licenses/validate?license_key=${encodeURIComponent(key)}`),
  activate: (key) => api.post(`/licenses/activate?license_key=${encodeURIComponent(key)}`),
};

// License Requests API (New System)
export const licenseRequestsApi = {
  getAll: (status) => api.get('/license-requests', { params: { status } }),
  getOne: (id) => api.get(`/license-requests/${id}`),
  create: (data) => api.post('/license-requests', data),
  approve: (id) => api.post(`/license-requests/${id}/approve`),
  reject: (id, reason) => api.post(`/license-requests/${id}/reject${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`),
};

// Document Folders API
export const documentFoldersApi = {
  getFolders: (pdvId) => api.get(`/pdv/${pdvId}/folders`),
  createFolder: (pdvId, data) => api.post(`/pdv/${pdvId}/folders`, data),
  deleteFolder: (folderId) => api.delete(`/folders/${folderId}`),
  createSubfolder: (folderId, data) => api.post(`/folders/${folderId}/subfolders`, data),
  deleteSubfolder: (subfolderId) => api.delete(`/subfolders/${subfolderId}`),
  getFolderDocuments: (folderId, subfolderId) => api.get(`/folders/${folderId}/documents`, { params: { subfolder_id: subfolderId } }),
  moveDocument: (docId, folderId, subfolderId) => api.put(`/documents/${docId}/move`, null, { params: { folder_id: folderId, subfolder_id: subfolderId } }),
  batchUpload: (pdvId, folderId, subfolderId, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post(`/pdv/${pdvId}/documents/batch-upload`, formData, {
      params: { folder_id: folderId, subfolder_id: subfolderId },
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Payments API
export const paymentsApi = {
  getPackages: () => api.get('/payments/packages'),
  createCheckout: (data) => api.post(`/payments/checkout?package_type=${data.package_type}&billing_cycle=${data.billing_cycle || 'monthly'}&customer_email=${encodeURIComponent(data.customer_email)}&customer_name=${encodeURIComponent(data.customer_name)}&origin_url=${encodeURIComponent(window.location.origin)}`),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// File upload
export const uploadFile = (folder, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/upload/${folder}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Excel Import API
export const importApi = {
  importPdvExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/pdv-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importPersonnelExcel: (file, pdvId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    const url = pdvId ? `/import/personnel-excel?pdv_id=${pdvId}` : '/import/personnel-excel';
    return api.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadTemplate: (type) => api.get(`/import/template/${type}`, { responseType: 'blob' }),
};

// Document Templates API
export const documentTemplatesApi = {
  getAll: (params) => api.get('/document-templates', { params }),
  create: (data) => api.post('/document-templates', data),
  delete: (id) => api.delete(`/document-templates/${id}`),
  generate: (data) => api.post('/generated-documents', data),
  getGenerated: (params) => api.get('/generated-documents', { params }),
  getGeneratedOne: (id) => api.get(`/generated-documents/${id}`),
  deleteGenerated: (id) => api.delete(`/generated-documents/${id}`),
  getGeneratedHtml: (id) => {
    const token = sessionStorage.getItem('token');
    return `/api/generated-documents/${id}/html?token=${token}`;
  },
  getGeneratedDownload: (id) => {
    const token = sessionStorage.getItem('token');
    return `/api/generated-documents/${id}/download?token=${token}`;
  },
  batchPrint: (docIds) => {
    const token = sessionStorage.getItem('token');
    return `/api/generated-documents/batch-print?token=${token}&doc_ids=${docIds.join(',')}`;
  },
  batchDownload: (docIds) => api.post('/generated-documents/batch-download', docIds, {
    params: { token: sessionStorage.getItem('token') },
    responseType: 'blob'
  }),
};

// Word Templates API
export const docxTemplatesApi = {
  getAll: () => api.get('/docx-templates'),
  upload: (formData, params) => api.post('/docx-templates/upload', formData, { 
    params,
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/docx-templates/${id}`),
  generate: (templateId, personnelId, additionalData = {}) => 
    api.post(`/docx-templates/${templateId}/generate`, null, {
      params: { 
        personnel_id: personnelId, 
        additional_data: JSON.stringify(additionalData) 
      }
    }),
};

// Medical Visits API
export const medicalVisitsApi = {
  getAll: (params) => api.get('/medical-visits', { params }),
  getExpiring: (days = 30) => api.get(`/medical-visits/expiring?days=${days}`),
  getOne: (id) => api.get(`/medical-visits/${id}`),
  create: (data) => api.post('/medical-visits', data),
  update: (id, data) => api.put(`/medical-visits/${id}`, data),
  delete: (id) => api.delete(`/medical-visits/${id}`),
  getHistory: (personnelId) => api.get(`/medical-visits/personnel/${personnelId}/history`),
};

// Training Materials API
export const trainingMaterialsApi = {
  getAll: (params) => api.get('/training-materials', { params }),
  create: (data) => api.post('/training-materials', data),
  update: (id, data) => api.put(`/training-materials/${id}`, data),
  delete: (id) => api.delete(`/training-materials/${id}`),
  trackDownload: (id) => api.put(`/training-materials/${id}/download`),
};

// Public Training Sessions API (Registro Presenze Pubblico)
export const trainingSessionsApi = {
  getAll: () => api.get('/training-sessions'),
  getOne: (id) => api.get(`/training-sessions/${id}`),
  create: (data) => api.post('/training-sessions', data),
  update: (id, data) => api.put(`/training-sessions/${id}`, data),
  delete: (id) => api.delete(`/training-sessions/${id}`),
  toggleActive: (id) => api.post(`/training-sessions/${id}/toggle-active`),
  deleteEnrollment: (sessionId, enrollmentId) =>
    api.delete(`/training-sessions/${sessionId}/enrollments/${enrollmentId}`),
  updateAttendance: (id, data) => api.put(`/training-sessions/${id}/attendance`, data),
  getDocxUrl: (id) => {
    const token = sessionStorage.getItem('token');
    return `/api/training-sessions/${id}/docx?token=${token}`;
  },
  getDocxOpnUrlForTemplate: (id, opnTemplateId) => {
    // v38b: registro OPN selezionato dall'utente (uno dei template OPN salvati)
    const token = sessionStorage.getItem('token');
    return `/api/training-sessions/${id}/docx-opn/${opnTemplateId}?token=${token}`;
  },
  getPdfUrl: (id) => {
    const token = sessionStorage.getItem('token');
    return `/api/training-sessions/${id}/pdf?token=${token}`;
  },
};

// Docente Online - Keys & Payments
export const docenteKeysApi = {
  myKeys: () => api.get('/docente-keys/me'),
  packages: () => api.get('/docente-keys/packages'),
  createCheckout: (data) => api.post('/docente-keys/checkout', data),
  getCheckoutStatus: (sessionId) => api.get(`/docente-keys/checkout/status/${sessionId}`),
  getUserKeys: (userId) => api.get(`/users/${userId}/docente-keys`),
  grantKeys: (userId, data) => api.post(`/users/${userId}/docente-keys/grant`, data),
  revokeKey: (userId, keyId) => api.delete(`/users/${userId}/docente-keys/${keyId}`),
};

// Training Tests API
export const trainingTestsApi = {
  list: () => api.get('/training-tests'),
  get: (id) => api.get(`/training-tests/${id}`),
  create: (data) => api.post('/training-tests', data),
  update: (id, data) => api.put(`/training-tests/${id}`, data),
  delete: (id) => api.delete(`/training-tests/${id}`),
  duplicate: (id) => api.post(`/training-tests/${id}/duplicate`),
  submit: (id, data) => api.post(`/training-tests/${id}/submit`, data),
  attempts: (id) => api.get(`/training-tests/${id}/attempts`),
  generatePublicLink: (id) => api.post(`/training-tests/${id}/generate-public-link`),
  revokePublicLink: (id) => api.delete(`/training-tests/${id}/public-link`),
};

// Public (no-auth) Training API
export const publicTrainingApi = {
  getSession: (token) => api.get(`/public/training/${token}`),
  enroll: (token, data) => api.post(`/public/training/${token}/enroll`, data),
  getTest: (token) => api.get(`/public/training-tests/${token}`),
  submitTest: (token, data) => api.post(`/public/training-tests/${token}/submit`, data),
};

// Register Templates API (admin DOCX template management)
export const registerTemplatesApi = {
  getAll: () => api.get('/register-templates'),
  upload: (file, name, description, isDefault) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name);
    if (description) fd.append('description', description);
    fd.append('is_default', isDefault ? 'true' : 'false');
    return api.post('/register-templates/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id, data) => api.put(`/register-templates/${id}`, data),
  delete: (id) => api.delete(`/register-templates/${id}`),
  getDownloadUrl: (id) => {
    const token = sessionStorage.getItem('token');
    return `/api/register-templates/${id}/download?token=${token}`;
  },
  // v36 - placeholder support
  getPlaceholders: (id) => api.get(`/register-templates/${id}/placeholders`),
  getPlaceholdersCatalog: () => api.get('/register-templates/placeholders/catalog'),
  generateForRegister: (templateId, registerId) =>
    api.post(`/register-templates/${templateId}/generate-for-register/${registerId}`, null, {
      responseType: 'blob',
    }),
};

// Training Types catalogue (for certificati)
export const trainingTypesApi = {
  getAll: () => api.get('/training-types'),
  create: (data) => api.post('/training-types', data),
  update: (id, data) => api.put(`/training-types/${id}`, data),
  delete: (id) => api.delete(`/training-types/${id}`),
};

// Training Certificates (certificati formazione già conseguita)
export const trainingCertificatesApi = {
  getAll: (params) => api.get('/training-certificates', { params }),
  getExpiring: (days = 60) => api.get('/training-certificates/expiring', { params: { days } }),
  upload: (file, meta) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(meta).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') fd.append(k, v);
    });
    return api.post('/training-certificates/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id, data) => api.put(`/training-certificates/${id}`, data),
  delete: (id) => api.delete(`/training-certificates/${id}`),
  getDownloadUrl: (id) => {
    const token = sessionStorage.getItem('token');
    return `/api/training-certificates/${id}/download?token=${token}`;
  },
  sendExpiryNotifications: (days = 60) =>
    api.post(`/training-certificates/check-expiries-and-notify?days=${days}`),
};

// Medical Visit Records
export const medicalRecordsApi = {
  getAll: (params) => api.get('/medical-records', { params }),
  upload: (file, meta) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(meta).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') fd.append(k, v);
    });
    return api.post('/medical-records/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id, data) => api.put(`/medical-records/${id}`, data),
  delete: (id) => api.delete(`/medical-records/${id}`),
  getDownloadUrl: (id) => {
    const token = sessionStorage.getItem('token');
    return `/api/medical-records/${id}/download?token=${token}`;
  },
};

// OCR (auto-extract fields from cert/visit document)
export const ocrApi = {
  extract: (file, docType = 'training_certificate') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    return api.post('/ocr/extract-certificate', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Personnel Reports (xlsx + pdf)
export const personnelReportsApi = {
  getPersonnelSummaryUrl: ({ pdvId, aziendaId, format = 'xlsx' } = {}) => {
    const token = sessionStorage.getItem('token');
    const params = new URLSearchParams({ format, token });
    if (pdvId) params.append('pdv_id', pdvId);
    if (aziendaId) params.append('azienda_id', aziendaId);
    return `/api/reports/personnel-summary?${params.toString()}`;
  },
};

// === Documents compliance matrix (Doc × UL) ===
export const documentsMatrixApi = {
  getMatrix: (params) => api.get('/documents-matrix', { params }),
  listTypes: () => api.get('/required-document-types'),
  createType: (data) => api.post('/required-document-types', data),
  deleteType: (id) => api.delete(`/required-document-types/${id}`),
};

// HACCP - Autocontrollo
export const haccpApi = {
  listTemplates: (kind) => api.get('/haccp-templates', { params: kind ? { kind } : {} }),
  uploadTemplate: (file, name, kind, isDefault = false, description = '') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name);
    fd.append('kind', kind);
    if (description) fd.append('description', description);
    fd.append('is_default', isDefault ? 'true' : 'false');
    return api.post('/haccp-templates/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteTemplate: (id) => api.delete(`/haccp-templates/${id}`),
  getPreferences: () => api.get('/haccp-templates/preferences/me'),
  setPreference: (kind, template_id) => api.put('/haccp-templates/preferences/me', { kind, template_id }),
  listRecords: (params) => api.get('/haccp-records', { params }),
  getRecord: (id) => api.get(`/haccp-records/${id}`),
  createRecord: (data) => api.post('/haccp-records', data),
  updateRecord: (id, data) => api.put(`/haccp-records/${id}`, data),
  deleteRecord: (id) => api.delete(`/haccp-records/${id}`),
  docxUrl: (id) => {
    const token = sessionStorage.getItem('token');
    return `/api/haccp-records/${id}/docx?token=${token}`;
  },
  pdfUrl: (id) => {
    const token = sessionStorage.getItem('token');
    return `/api/haccp-records/${id}/pdf?token=${token}`;
  },
  excelUrl: (id) => {
    const token = sessionStorage.getItem('token');
    return `/api/haccp-records/${id}/excel?token=${token}`;
  },
};

// v38c: Programmi Sicurezza (Generale, Specifico, Antincendio, Primo Soccorso, ...)
export const safetyProgramsApi = {
  getAll: () => api.get('/safety-programs'),
  getOne: (id) => api.get(`/safety-programs/${id}`),
  create: (data) => api.post('/safety-programs', data),
  update: (id, data) => api.put(`/safety-programs/${id}`, data),
  delete: (id) => api.delete(`/safety-programs/${id}`),
  restoreBuiltins: () => api.post('/safety-programs/restore-builtins'),
};


// v38d: Modulo Studio Tecnico (ERP per studi architettura/ingegneria)
export const studioApi = {
  // Dashboard
  dashboard: () => api.get('/studio/dashboard'),
  // Clienti
  getClienti: () => api.get('/studio/clienti'),
  createCliente: (data) => api.post('/studio/clienti', data),
  updateCliente: (id, data) => api.put(`/studio/clienti/${id}`, data),
  deleteCliente: (id) => api.delete(`/studio/clienti/${id}`),
  // Commesse
  getCommesse: () => api.get('/studio/commesse'),
  getCommessa: (id) => api.get(`/studio/commesse/${id}`),
  createCommessa: (data) => api.post('/studio/commesse', data),
  updateCommessa: (id, data) => api.put(`/studio/commesse/${id}`, data),
  deleteCommessa: (id) => api.delete(`/studio/commesse/${id}`),
  // Preventivi
  getPreventivi: () => api.get('/studio/preventivi'),
  createPreventivo: (data) => api.post('/studio/preventivi', data),
  updatePreventivo: (id, data) => api.put(`/studio/preventivi/${id}`, data),
  deletePreventivo: (id) => api.delete(`/studio/preventivi/${id}`),
  convertPreventivo: (id) => api.post(`/studio/preventivi/${id}/convert-to-commessa`),
  downloadPreventivoPdf: (id) => api.get(`/studio/preventivi/${id}/pdf`, { responseType: 'blob' }),
  // Pagamenti
  getPagamenti: (commessaId) => api.get('/studio/pagamenti', { params: commessaId ? { commessa_id: commessaId } : {} }),
  createPagamento: (data) => api.post('/studio/pagamenti', data),
  updatePagamento: (id, data) => api.put(`/studio/pagamenti/${id}`, data),
  deletePagamento: (id) => api.delete(`/studio/pagamenti/${id}`),
  // Scadenze
  getScadenze: (commessaId) => api.get('/studio/scadenze', { params: commessaId ? { commessa_id: commessaId } : {} }),
  createScadenza: (data) => api.post('/studio/scadenze', data),
  updateScadenza: (id, data) => api.put(`/studio/scadenze/${id}`, data),
  deleteScadenza: (id) => api.delete(`/studio/scadenze/${id}`),
  // v38f: Import visura camerale per cliente studio
  importCameraleCliente: (clienteId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/studio/clienti/${clienteId}/import-camerale`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // v38f: Insegne & Affissioni
  getInsegne: () => api.get('/studio/insegne'),
  createInsegna: (data) => api.post('/studio/insegne', data),
  updateInsegna: (id, data) => api.put(`/studio/insegne/${id}`, data),
  deleteInsegna: (id) => api.delete(`/studio/insegne/${id}`),
  // v38f: Professionista
  getProfessionista: () => api.get('/studio/professionista'),
  upsertProfessionista: (data) => api.put('/studio/professionista', data),
  // v38f: SAL (Stato Avanzamento Lavori) per commessa
  getSal: (commessaId) => api.get('/studio/sal', { params: commessaId ? { commessa_id: commessaId } : {} }),
  createSal: (data) => api.post('/studio/sal', data),
  updateSal: (id, data) => api.put(`/studio/sal/${id}`, data),
  deleteSal: (id) => api.delete(`/studio/sal/${id}`),
  // v38f: Servizi erogati per commessa
  getServizi: (commessaId) => api.get('/studio/servizi', { params: commessaId ? { commessa_id: commessaId } : {} }),
  createServizio: (data) => api.post('/studio/servizi', data),
  updateServizio: (id, data) => api.put(`/studio/servizi/${id}`, data),
  deleteServizio: (id) => api.delete(`/studio/servizi/${id}`),
  // v39: Fatture elettroniche (FatturaPA SDI)
  getFatture: (commessaId) => api.get('/studio/fatture', { params: commessaId ? { commessa_id: commessaId } : {} }),
  createFattura: (data) => api.post('/studio/fatture', data),
  updateFattura: (id, data) => api.put(`/studio/fatture/${id}`, data),
  deleteFattura: (id) => api.delete(`/studio/fatture/${id}`),
  downloadFatturaXml: (id) => api.get(`/studio/fatture/${id}/xml-sdi`, { responseType: 'blob' }),
  downloadFatturaPdf: (id) => api.get(`/studio/fatture/${id}/pdf`, { responseType: 'blob' }),
  validateFattura: (id) => api.get(`/studio/fatture/${id}/validate`),
  validateAllCodes: () => api.get('/studio/validate-codes'),
  markFatturaEmessa: (id) => api.post(`/studio/fatture/${id}/mark-emessa`),
  // v50: Modulistica Pratiche - Template engine
  getTemplates: (filters = {}) => api.get('/studio/templates/', { params: filters }),
  getTemplate: (id) => api.get(`/studio/templates/${id}`),
  getTemplateTipi: () => api.get('/studio/templates/tipi'),
  getTemplatePlaceholders: () => api.get('/studio/templates/_meta/placeholders-disponibili'),
  uploadTemplate: (formData) => api.post('/studio/templates/', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteTemplate: (id) => api.delete(`/studio/templates/${id}`),
  generateTemplate: (id, payload) => api.post(`/studio/templates/${id}/generate`, payload, { responseType: 'blob' }),
  downloadTemplateOriginal: (id) => api.get(`/studio/templates/${id}/download-template`, { responseType: 'blob' }),
};

// v52: HACCP Schede Tecniche
export const haccpSchedeApi = {
  extractLabels: (formData) => api.post('/haccp/schede/extract-labels', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getProdotti: () => api.get('/haccp/schede/prodotti'),
  getProdotto: (id) => api.get(`/haccp/schede/prodotti/${id}`),
  createProdotto: (data) => api.post('/haccp/schede/prodotti', data),
  bulkCreateProdotti: (arr) => api.post('/haccp/schede/prodotti/bulk', arr),
  updateProdotto: (id, data) => api.put(`/haccp/schede/prodotti/${id}`, data),
  deleteProdotto: (id) => api.delete(`/haccp/schede/prodotti/${id}`),
  generateScheda: (id) => api.post(`/haccp/schede/prodotti/${id}/generate`, {}, { responseType: 'blob' }),
  uploadFoto: (id, formData) => api.post(`/haccp/schede/prodotti/${id}/foto`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  bulkUploadZip: (formData) => api.post('/haccp/schede/foto/bulk-zip', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteFoto: (id, idx) => api.delete(`/haccp/schede/prodotti/${id}/foto/${idx}`),
  recropFoto: (id) => api.post(`/haccp/schede/prodotti/${id}/foto/recrop`),
  aiSuggestCaratteristiche: (data) => api.post('/haccp/schede/ai/suggest-caratteristiche', data),
  getFotoUrl: (id, idx) => `${api.defaults.baseURL}/haccp/schede/prodotti/${id}/foto/${idx}`,
  getAzienda: () => api.get('/haccp/schede/azienda'),
  saveAzienda: (data) => api.put('/haccp/schede/azienda', data),
  syncAziendaFrom: (aziendaId) => api.post(`/haccp/schede/azienda/sync-from/${aziendaId}`),
  uploadLogo: (formData) => api.post('/haccp/schede/azienda/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getLogoUrl: () => `${api.defaults.baseURL}/haccp/schede/azienda/logo`,
  deleteLogo: () => api.delete('/haccp/schede/azienda/logo'),
};

// v54: Helper per recuperare aziende da Gestione Aziendale (per sync)
export const aziendeApi = {
  list: () => api.get('/aziende'),
};

// v75: Verbali di Prelievo + Laboratori Analisi
export const verbaliPrelievoApi = {
  list: () => api.get('/verbali-prelievo'),
  get: (id) => api.get(`/verbali-prelievo/${id}`),
  create: (data) => api.post('/verbali-prelievo', data),
  delete: (id) => api.delete(`/verbali-prelievo/${id}`),
  generateDocx: (id) => api.post(`/verbali-prelievo/${id}/generate-docx`, {}, { responseType: 'blob' }),
  sendToLab: (id) => api.post(`/verbali-prelievo/${id}/send-to-lab`),
  uploadReferto: (id, formData) => api.post(`/verbali-prelievo/${id}/upload-referto`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadReferto: (id) => api.get(`/verbali-prelievo/${id}/referto`, { responseType: 'blob' }),
};
export const laboratoriApi = {
  list: () => api.get('/laboratori-analisi'),
  create: (data) => api.post('/laboratori-analisi', data),
  delete: (id) => api.delete(`/laboratori-analisi/${id}`),
};


export const haccpLicensesApi = {
  myStatus: () => api.get('/haccp-licenses/my-status'),
  list: () => api.get('/haccp-licenses'),
  generate: (payload) => api.post('/haccp-licenses/generate', payload),
  activate: (license_key) => api.post('/haccp-licenses/activate', { license_key }),
  delegate: (user_id, enable) => api.post('/haccp-licenses/delegate', { user_id, enable }),
  myDelegates: () => api.get('/haccp-licenses/my-delegates'),
  stripeCheckout: (license_type = 'admin', quantity = 1) =>
    api.post(`/haccp-licenses/stripe-checkout?license_type=${license_type}&quantity=${quantity}`),
  stripeConfirm: (session_id) => api.post(`/haccp-licenses/stripe-confirm?session_id=${session_id}`),
};

export default api;
